import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Brain, Users, Lock, CheckCircle, ArrowRight, Zap, Eye, RotateCcw,
  FileText, Layers, Activity, Globe, Server, Code, Database, Clock,
  TrendingUp, Award, Settings, Terminal, GitBranch, Bell, BarChart3,
  Fingerprint, Key, RefreshCw, Workflow, Target, Sparkles, Network, Cpu
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';

export default function FeaturesPage() {
  const [activeCategory, setActiveCategory] = useState('governance');

  const categories = [
    { id: 'governance', name: 'Governance Engine', icon: Shield },
    { id: 'audit', name: 'Audit & Compliance', icon: FileText },
    { id: 'security', name: 'Security', icon: Lock },
    { id: 'integration', name: 'Integration', icon: Network },
  ];

  const featuresByCategory = {
    governance: [
      {
        icon: Shield,
        title: 'Executive Council™ Architecture',
        description: 'Distributed decision-making across CEO, CTO, CPO, and CAO agents. No single agent can unilaterally deploy autonomy.',
        details: ['Multi-agent consensus', 'Veto authority', 'Escalation protocols', 'Human override'],
        color: 'blue',
      },
      {
        icon: Layers,
        title: 'Five-Tier Autonomy Model™',
        description: 'Graduated control from observational to fully autonomous. Configure precise autonomy levels per agent, action, or context.',
        details: ['Tier 1-5 classification', 'Dynamic adjustment', 'Context-aware routing', 'Policy enforcement'],
        color: 'purple',
      },
      {
        icon: Workflow,
        title: 'Policy Engine (ABAC)',
        description: 'Attribute-Based Access Control with custom governance rules. Define complex policies using natural language or code.',
        details: ['Custom policy rules', 'Real-time evaluation', 'Policy versioning', 'Conflict resolution'],
        color: 'cyan',
      },
      {
        icon: RotateCcw,
        title: 'Instant Rollback',
        description: 'Pause any agent and reverse actions in milliseconds. Automatic state snapshots ensure nothing is lost.',
        details: ['One-click rollback', 'State preservation', 'Cascading reversal', 'Audit trail'],
        color: 'green',
      },
    ],
    audit: [
      {
        icon: FileText,
        title: 'Immutable Audit Logs',
        description: 'Every decision, action, and outcome logged with cryptographic verification. 7-year retention for enterprise compliance.',
        details: ['Tamper-proof logs', '7-year retention', 'Cryptographic hashes', 'Chain of custody'],
        color: 'amber',
      },
      {
        icon: BarChart3,
        title: 'Analytics Dashboard',
        description: 'Real-time visibility into agent behavior, decision patterns, and governance metrics.',
        details: ['Real-time metrics', 'Decision analytics', 'Behavioral trends', 'Custom reports'],
        color: 'blue',
      },
      {
        icon: Bell,
        title: 'Alerting & Monitoring',
        description: 'Proactive alerts for policy violations, anomalous behavior, and governance breaches.',
        details: ['Anomaly detection', 'Custom thresholds', 'Multi-channel alerts', 'Incident management'],
        color: 'rose',
      },
      {
        icon: Target,
        title: 'Compliance Reporting',
        description: 'Pre-built reports designed for SOC-2, ISO 27001, HIPAA, and GDPR requirements. Export-ready for auditors.',
        details: ['SOC-2 ready', 'ISO 27001 ready', 'HIPAA ready', 'GDPR ready'],
        color: 'purple',
      },
    ],
    security: [
      {
        icon: Lock,
        title: 'Zero Trust Architecture',
        description: 'Every request authenticated and authorized. No implicit trust, even for internal services.',
        details: ['mTLS everywhere', 'Service mesh', 'Least privilege', 'Continuous verification'],
        color: 'green',
      },
      {
        icon: Key,
        title: 'Enterprise SSO',
        description: 'Seamless integration with Okta, Azure AD, Google Workspace, Auth0, and LDAP.',
        details: ['SAML 2.0', 'OIDC support', 'MFA enforcement', 'Role mapping'],
        color: 'blue',
      },
      {
        icon: Fingerprint,
        title: 'Encryption',
        description: 'AES-256 encryption at rest, TLS 1.3 in transit. Customer-managed keys available.',
        details: ['AES-256 at rest', 'TLS 1.3 in transit', 'BYOK support', 'HSM integration'],
        color: 'purple',
      },
      {
        icon: Shield,
        title: 'Prompt Injection Protection',
        description: 'Hardened against prompt injection, jailbreaks, and adversarial inputs. Multi-layer defense.',
        details: ['Input validation', 'Output filtering', 'Behavioral bounds', 'Anomaly detection'],
        color: 'cyan',
      },
    ],
    integration: [
      {
        icon: Code,
        title: 'REST & GraphQL APIs',
        description: 'Comprehensive APIs for every platform capability. OpenAPI 3.0 specification available.',
        details: ['REST API v1', 'GraphQL endpoint', 'Webhooks', 'Rate limiting'],
        color: 'blue',
      },
      {
        icon: Terminal,
        title: 'SDKs',
        description: 'Native SDKs for Python, Node.js, Go, and Java. Type-safe and fully documented.',
        details: ['Python SDK', 'Node.js SDK', 'Go SDK', 'Java SDK'],
        color: 'green',
      },
      {
        icon: GitBranch,
        title: 'CI/CD Integration',
        description: 'GitHub Actions, GitLab CI, Jenkins, and CircleCI integrations out of the box.',
        details: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'CircleCI'],
        color: 'purple',
      },
      {
        icon: Server,
        title: 'Deployment Options',
        description: 'SaaS, private cloud, or on-premise. Kubernetes-native with Helm charts.',
        details: ['Multi-tenant SaaS', 'Private cloud', 'On-premise', 'Air-gapped'],
        color: 'amber',
      },
    ],
  };

  const platformServices = [
    {
      icon: Cpu,
      name: 'Orchestrator Service',
      description: 'Request routing and multi-agent coordination',
      tech: 'Node.js / Redis',
    },
    {
      icon: Shield,
      name: 'Policy Engine',
      description: 'ABAC with custom governance rules',
      tech: 'Rust / PostgreSQL',
    },
    {
      icon: Zap,
      name: 'Tool Gateway',
      description: 'Single execution chokepoint with validation',
      tech: 'Go / gRPC',
    },
    {
      icon: FileText,
      name: 'Audit Service',
      description: 'Immutable decision logging',
      tech: 'TimescaleDB',
    },
    {
      icon: Database,
      name: 'Memory Service',
      description: 'Tenant-scoped agent memory with PII redaction',
      tech: 'Pinecone / Redis',
    },
    {
      icon: Brain,
      name: 'CAO Runtime',
      description: 'Claude Opus 4.5 with behavioral constraints',
      tech: 'Anthropic SDK',
    },
  ];

  return (
    <div className="relative min-h-screen pt-20">
      <AnimatedBackground variant="cyber" />
      <ParticleBackground density={30} />

      {/* Hero Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
              <Zap className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400 font-medium">Platform Capabilities</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-white">Enterprise-Grade</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                Features
              </span>
            </h1>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Everything you need to govern autonomous AI systems at scale.
              Built for security, compliance, and reliability from day one.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-16">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700'
                }`}
              >
                <category.icon className="w-5 h-5" />
                {category.name}
              </button>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {featuresByCategory[activeCategory].map((feature, i) => (
              <GlowingCard key={i} glowColor={feature.color}>
                <div className="p-8">
                  <div className={`w-14 h-14 rounded-xl bg-${feature.color}-500/10 border border-${feature.color}-500/20 flex items-center justify-center mb-6`}>
                    <feature.icon className={`w-7 h-7 text-${feature.color}-400`} />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-slate-400 mb-6">{feature.description}</p>

                  <div className="grid grid-cols-2 gap-2">
                    {feature.details.map((detail, j) => (
                      <div key={j} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                        {detail}
                      </div>
                    ))}
                  </div>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Architecture Section */}
      <section className="relative py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Platform</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Architecture
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Microservices architecture built for scale, resilience, and security.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformServices.map((service, i) => (
              <GlowingCard key={i} glowColor="cyan">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-cyan-400" />
                    </div>
                    <span className="text-xs font-mono text-slate-500 bg-slate-800 px-2 py-1 rounded">
                      {service.tech}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-sm text-slate-400">{service.description}</p>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Enterprise Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Built for</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Enterprise
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Powered by proprietary AI governance technology developed exclusively by NeuralQuantum.ai LLC.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* AI Governance */}
            <GlowingCard glowColor="purple">
              <div className="p-8">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <Brain className="w-6 h-6 text-purple-400" />
                  AI Governance Engine
                </h3>
                <div className="space-y-4">
                  {[
                    { name: 'Chief Agentic Officer™', desc: 'Executive-level AI governance persona' },
                    { name: 'Executive Council™', desc: 'Multi-agent distributed decision authority' },
                    { name: 'Five-Tier Autonomy Model™', desc: 'Graduated control and trust framework' },
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-slate-800/50 rounded-lg">
                      <div className="font-medium text-white mb-1">{item.name}</div>
                      <div className="text-sm text-slate-400">{item.desc}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-4 italic">
                  Patent pending technology. All rights reserved.
                </p>
              </div>
            </GlowingCard>

            {/* Enterprise Infrastructure */}
            <GlowingCard glowColor="blue">
              <div className="p-8">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <Server className="w-6 h-6 text-blue-400" />
                  Enterprise Infrastructure
                </h3>
                <div className="space-y-4">
                  {[
                    { name: 'Cloud-Native Architecture', desc: 'Deploy on any major cloud provider or on-premise' },
                    { name: 'Enterprise-Grade Security', desc: 'Built to meet SOC-2, ISO 27001, HIPAA, GDPR standards' },
                    { name: 'High Availability', desc: '99.9% uptime SLA with global distribution' },
                    { name: 'Scalable by Design', desc: 'From startup to Fortune 500 scale' },
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-slate-800/50 rounded-lg">
                      <div className="font-medium text-white mb-1">{item.name}</div>
                      <div className="text-sm text-slate-400">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </GlowingCard>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to see it in action?
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Schedule a personalized demo with our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all"
            >
              Request Demo
            </Link>
            <Link
              to="/docs"
              className="px-8 py-4 rounded-xl font-semibold border border-slate-700 hover:border-blue-500/50 text-slate-300 hover:text-white transition-all"
            >
              Read Documentation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
